import React from 'react';
import { Mail, Camera, BrainCircuit, Bot, Zap } from 'lucide-react';
import { LeadFormModal } from '../components/LeadFormModal';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { Breadcrumb } from '../components/Breadcrumb';
import { WhatNextSection } from '../components/WhatNextSection';
import { ParticlesBackground } from '../components/effects/ParticlesBackground';
import { AnimatedBlob } from '../components/effects/AnimatedBlob';
import { AnimatedGrid } from '../components/effects/AnimatedGrid';
import { RevealOnScroll } from '../components/effects/RevealOnScroll';
import { FeatureCard } from '../components/ui/FeatureCard';

export const SmartClaimAiPage: React.FC = () => {
    usePageMetadata({
        title: "SmartClaim AI | Automatyzacja Reklamacji w E-commerce",
        description: "Zaoszczędź 80% czasu na obsłudze reklamacji. SmartClaim AI analizuje zgłoszenia i zdjęcia, a następnie automatycznie generuje odpowiedzi. Zobacz demo.",
        schema: [
            {
                "@context": "https://schema.org",
                "@type": "Product",
                "name": "SmartClaim AI",
                "description": "System AI, który analizuje zgłoszenia reklamacyjne, ocenia uszkodzenia na podstawie zdjęć i generuje odpowiedzi, oszczędzając do 80% czasu pracy.",
                "brand": {
                    "@type": "Brand",
                    "name": "BiznesBezKlikania.pl"
                },
                "category": "Software > AI Automation > E-commerce",
                "url": "https://biznesbezklikania.pl/rozwiazania/smartclaim-ai",
                "image": "https://biznesbezklikania.pl/og-image.png",
                "offers": {
                    "@type": "Offer",
                    "availability": "https://schema.org/InStock",
                    "priceCurrency": "PLN"
                },
                "aggregateRating": {
                    "@type": "AggregateRating",
                    "ratingValue": "4.8",
                    "reviewCount": "32",
                    "bestRating": "5"
                }
            },
            {
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "SmartClaim AI",
                "applicationCategory": "BusinessApplication",
                "operatingSystem": "Cloud-based"
            }
        ],
        ogData: {
            type: 'product'
        }
    });

    const features = [
        { icon: <Mail className="w-8 h-8" />, title: 'Inteligentny Parser Maili', description: 'AI automatycznie odczytuje i kategoryzuje maile reklamacyjne, wyciągając kluczowe informacje.' },
        { icon: <Camera className="w-8 h-8" />, title: 'Analiza Zdjęć AI Vision', description: 'Nasza sieć neuronowa ocenia zdjęcia uszkodzeń, odróżniając wady fabryczne od uszkodzeń mechanicznych.' },
        { icon: <BrainCircuit className="w-8 h-8" />, title: 'Silnik Decyzyjny', description: 'System podejmuje decyzje o uznaniu lub odrzuceniu reklamacji na podstawie Twoich wytycznych i prawa.' },
        { icon: <Bot className="w-8 h-8" />, title: 'Generator Odpowiedzi', description: 'AI tworzy spersonalizowane, empatyczne odpowiedzi, utrzymując profesjonalny ton i informując klienta.' },
    ];

    return (
        <>
            {/* Hero Section */}
            <section className="relative min-h-[60vh] flex items-center overflow-hidden py-20 md:py-24">
                <ParticlesBackground variant="minimal" />
                <AnimatedBlob variant="purple" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-bkg-light/50 to-bkg-light dark:via-bkg-dark/50 dark:to-bkg-dark pointer-events-none" />

                <div className="container mx-auto px-4 text-center relative z-10">
                    <RevealOnScroll>
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 dark:bg-white/5 backdrop-blur-sm border border-white/20 dark:border-white/10 mb-6">
                            <Zap className="w-4 h-4 text-primary-light dark:text-primary-dark" />
                            <span className="text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark">
                                Automatyzacja E-commerce
                            </span>
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark bg-clip-text text-transparent bg-[length:200%_200%] animate-gradient">
                            SmartClaim AI
                        </h1>
                        <p className="text-sm font-semibold text-primary-light dark:text-primary-dark uppercase tracking-wider mb-4">
                            Automatyzacja Reklamacji
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={100}>
                        <p className="text-lg md:text-xl text-text-secondary-light dark:text-text-secondary-dark max-w-3xl mx-auto mb-10">
                            Zaoszczędź 80% czasu na obsłudze reklamacji. Nasze AI analizuje zgłoszenia i zdjęcia, a następnie automatycznie generuje odpowiedzi w minuty, nie dni.
                        </p>
                    </RevealOnScroll>

                    <RevealOnScroll delay={200}>
                        <LeadFormModal
                            productSlug="smartclaim-ai"
                            triggerLabel="Umów bezpłatne demo"
                            triggerClassName="px-8 py-4 font-semibold text-white bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark rounded-xl hover:shadow-xl hover:shadow-primary-light/30 dark:hover:shadow-primary-dark/30 hover:scale-[1.02] transition-all duration-300 text-lg inline-flex items-center group animate-glow"
                        />
                    </RevealOnScroll>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-bkg-light dark:from-bkg-dark to-transparent pointer-events-none" />
            </section>

            <Breadcrumb />

            {/* Features Section */}
            <section className="py-16 md:py-20 relative">
                <AnimatedGrid variant="dots" animated={false} />
                <div className="container mx-auto px-4 relative z-10">
                    <RevealOnScroll className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark">
                            Koniec z ręczną obsługą reklamacji
                        </h2>
                        <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark mt-4 max-w-2xl mx-auto">
                            Zaoszczędź do 80% czasu i zredukuj koszty obsługi, jednocześnie zwiększając satysfakcję klientów.
                        </p>
                    </RevealOnScroll>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => (
                            <RevealOnScroll key={feature.title} delay={index * 100}>
                                <FeatureCard
                                    icon={feature.icon}
                                    title={feature.title}
                                    description={feature.description}
                                />
                            </RevealOnScroll>
                        ))}
                    </div>
                </div>
            </section>

            <WhatNextSection diyLink="/rozwiazania/smartclaim-ai" />
        </>
    );
};
